import uuidv4 from "uuid/v4";
import { NamedStack } from "./named-stack";

/**
 * Author: Arnaud PEZEL
 * Used for stacked components like modals
 * Usage example :
 * ```
 *    class A extends Vue {
 *
 *      protected stackSys: StackSystem = new StackSystem();
 *
 *      onClickAddElement() {
 *        this.stackSys.push(destroy => (
 *          <div onClick={destroy}>Hello !</div>
 *        ));
 *      }
 *      
 *       onClickAddAsyncElement() {
 *        this.stackSys.push(aync destroy => {
 *          const greetings = await getGreetings();
 *          return <div onClick={destroy}>{greetings}</div>
 *        });
 *      }
 *
 *      onClickUpsertElement() {
 *        this.stackSys.push(destroy => (
 *          <div onClick={destroy}>Hello !</div>
 *        ), { slot: "slot1" });
 *      }
 *
 *      onClickToggleElement() {
 *        this.stackSys.toggle("slot2", destroy => (
 *          <div onClick={destroy}>Hello, i'm toggleable !</div>
 *        ));
 *      }
 *
 *      render() {
 *        return (
 *          <div>
 *            <a onClick={this.onClickAddElement}>Add element</a>
 *            <a onClick={this.onClickAddAsyncElement}>Add async</a>
 *            <a onClick={this.onClickUpsertElement}>Upsert element</a>
 *            <a onClick={this.onClickToggleElement}>Toggle element</a>
 *            <div>{this.stackSys.$}</div>
 *          </div>
 *        );
 *      }
 *    }
 * ```
 */
export class StackSystem {

  protected stack: NamedStack = new NamedStack();
  protected fwm: WeakMap<Function, {destroy: Function, obj: any}> = new WeakMap();

  public get $() {
    return this.stack.map(({obj}) => obj);
  }

  public has(slot): boolean {
    return this.stack.hasNamed(slot);
  }

  public async push(fn: (destroy) => any, { slot = null, onDestroy = null } = {}): Promise<StackSystem> {
    !slot && (slot = uuidv4());
    let destroy = () => {
      this.stack.remove(this.fwm.get(fn));
      this.fwm.delete(fn);
      onDestroy?.();
    };
    const el = {destroy, obj: await fn(destroy)};
    this.stack.uppush(slot, el, ({destroy}, nw) => {
      destroy();
      this.stack.push(slot, nw);
    });
    this.fwm.set(fn, el);
    return this;
  }

  public pop(slot): StackSystem {
    if (this.stack.hasNamed(slot)) {
      this.stack.removeNamed(slot);
    }
    return this;
  }

  public async toggle(slot, fn: (destroy) => any, { ...args } = {}): Promise<StackSystem> {
    if (this.stack.hasNamed(slot)) {
      return this.pop(slot);
    } else {
      return this.push(fn, { ...args, slot })
    }
  }

};